/* Allorn 2026 — homepage behaviour: reveals, 3D hero, rails, story highlight.
   Animates transform/opacity only, pauses loops off-screen, honours reduced motion. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  const initReveals = (root = document) =>
    root.querySelectorAll('.al-reveal:not(.is-in)').forEach((el) => {
      if (reduceMotion || el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  // Safety net: never leave content hidden if an observer callback is missed.
  setTimeout(() => document.querySelectorAll('.al-reveal:not(.is-in)').forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight * 1.5) el.classList.add('is-in');
  }), 1200);
  window.addEventListener('scroll', () => document.querySelectorAll('.al-reveal:not(.is-in)').forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight * 0.95) el.classList.add('is-in');
  }), { passive: true });

  /* ---------- Pause looping motion when off-screen ---------- */
  const visibilityObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      entry.target.classList.toggle('al-paused', !entry.isIntersecting);
    });
  });

  /* ---------- Hero: Seedance orbit video that turns with scroll ---------- */
  const initHero = (hero) => {
    visibilityObserver.observe(hero);
    const media = hero.querySelector('.al-stage__media');
    const video = hero.querySelector('.al-stage__video');
    if (!media || !video) return;

    if (media.dataset.mode === 'loop') {
      if (reduceMotion) video.removeAttribute('autoplay'), video.pause();
      else {
        // Some browsers block autoplay until data is ready or the visitor interacts: retry.
        const kick = () => video.play().catch(() => {});
        video.addEventListener('canplay', kick, { once: true });
        ['pointerdown', 'touchstart', 'scroll'].forEach((ev) => window.addEventListener(ev, kick, { once: true, passive: true }));
      }
      new IntersectionObserver(([entry]) => {
        if (reduceMotion) return;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      }).observe(hero);
      return;
    }

    // Scrub mode: scroll position drives the video's playhead.
    if (reduceMotion) return;
    let frame = 0;
    let target = 0;
    let current = 0;
    const tick = () => {
      frame = 0;
      if (!video.duration) return;
      current += (target - current) * 0.18;
      if (Math.abs(target - current) < 0.001) current = target;
      video.currentTime = current * (video.duration - 0.05);
      if (current !== target) frame = requestAnimationFrame(tick);
    };
    const update = () => {
      const rect = hero.getBoundingClientRect();
      const travel = rect.height + window.innerHeight * 0.2;
      target = Math.min(Math.max((window.innerHeight * 0.2 - rect.top) / travel, 0), 1);
      if (!frame) frame = requestAnimationFrame(tick);
    };
    video.addEventListener('loadedmetadata', update);
    window.addEventListener('scroll', update, { passive: true });
    update();
  };

  /* ---------- Horizontal rail ---------- */
  const initRail = (rail) => {
    const track = rail.querySelector('.al-rail__track');
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');
    if (!track || !prev || !next) return;
    const step = () => Math.max(track.clientWidth * 0.8, 280);
    const sync = () => {
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  };

  /* ---------- Story: highlight words as you scroll ---------- */
  const initStory = (story) => {
    const text = story.querySelector('.al-story__text');
    if (!text || text.dataset.split) return;
    text.dataset.split = '1';
    const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) return frag.append(part);
        const span = document.createElement('span');
        span.className = 'w';
        span.textContent = part;
        frag.append(span);
      });
      node.replaceWith(frag);
    });
    const words = [...text.querySelectorAll('.w')];
    if (reduceMotion) return words.forEach((w) => w.classList.add('on'));

    let frame = 0;
    let active = false;
    const update = () => {
      frame = 0;
      const rect = text.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(Math.max((vh * 0.85 - rect.top) / (rect.height + vh * 0.35), 0), 1);
      const lit = Math.round(progress * words.length);
      words.forEach((w, i) => w.classList.toggle('on', i < lit));
    };
    const queue = () => { if (active && !frame) frame = requestAnimationFrame(update); };
    new IntersectionObserver(([entry]) => { active = entry.isIntersecting; queue(); }).observe(story);
    window.addEventListener('scroll', queue, { passive: true });
  };

  /* ---------- Spotlight cover tilt ---------- */
  const initSpotlight = (spot) => {
    const media = spot.querySelector('.al-spot__media');
    const cover = spot.querySelector('.al-spot__cover');
    if (!media || !cover || reduceMotion) return;
    media.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const rect = media.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      cover.style.setProperty('--ry', `${x * 16}deg`);
      cover.style.setProperty('--rx', `${-y * 12}deg`);
    });
    media.addEventListener('pointerleave', () => {
      cover.style.removeProperty('--ry');
      cover.style.removeProperty('--rx');
    });
  };

  const init = (root = document) => {
    initReveals(root);
    root.querySelectorAll('[data-al-hero]').forEach(initHero);
    root.querySelectorAll('[data-al-rail]').forEach(initRail);
    root.querySelectorAll('[data-al-story]').forEach(initStory);
    root.querySelectorAll('[data-al-spot]').forEach(initSpotlight);
    root.querySelectorAll('.al-marquee').forEach((m) => visibilityObserver.observe(m));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-run when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
